import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Stack,
  Box,
  IconButton,
  Chip,
  Typography,
  Grid
} from '@mui/material';
import { Close as CloseIcon, Add as AddIcon } from '@mui/icons-material';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { useDropzone } from 'react-dropzone';
import { Event, EventCategory, LocationType, EventStatus, TierClassification } from '@/types/events';

interface EventFormProps {
  open: boolean;
  event: Event | null;
  onClose: () => void;
  onSave: (eventData: Partial<Event>) => void;
}

const EVENT_TYPES: { value: EventCategory; label: string }[] = [
  { value: 'workshop', label: 'Workshop' },
  { value: 'seminar', label: 'Seminar' },
  { value: 'social', label: 'Social Event' },
  { value: 'meeting', label: 'Meeting' },
  { value: 'training', label: 'Training' }
];

const TIER_CLASSIFICATIONS: { value: TierClassification; label: string }[] = [
  { value: 'universal', label: 'Universal' },
  { value: 'targeted', label: 'Targeted' },
  { value: 'intensive', label: 'Intensive' }
];

const LOCATION_TYPES: { value: LocationType; label: string }[] = [
  { value: 'physical', label: 'Physical' },
  { value: 'virtual', label: 'Virtual' },
  { value: 'hybrid', label: 'Hybrid' }
];

const EVENT_STATUSES: { value: EventStatus; label: string }[] = [
  { value: 'draft', label: 'Draft' },
  { value: 'pending_approval', label: 'Pending Approval' },
  { value: 'approved', label: 'Approved' },
  { value: 'in_progress', label: 'In Progress' },
  { value: 'completed', label: 'Completed' },
  { value: 'cancelled', label: 'Cancelled' }
];

const GRADES = Array.from({ length: 4 }, (_, i) => i + 9); // Grades 9-12

const INITIAL_FORM_DATA: Partial<Event> = {
  title: '',
  description: '',
  startDate: new Date().toISOString(),
  endDate: new Date().toISOString(),
  locationType: 'physical',
  tierClassification: 'universal',
  location: '',
  virtualLink: '',
  eventCategory: 'workshop',
  status: 'draft',
  objectives: [],
  targetGrades: [],
  eventResources: [],
  staffAssignments: [],
  frequencyType: 'one_time',
  occurrences: [],
  budget: [],
  tasks: [],
  resources: [],
  feedback: [],
  compliance: [],
  metrics: {
    attendance: 0,
    satisfaction: 0,
    budget: {
      estimated: 0,
      actual: 0,
      variance: 0,
    }
  }
};

export const EventForm: React.FC<EventFormProps> = ({
  open,
  event,
  onClose,
  onSave
}) => {
  const [formData, setFormData] = useState<Partial<Event>>(INITIAL_FORM_DATA);

  useEffect(() => {
    if (event) {
      setFormData(event);
    } else {
      setFormData(INITIAL_FORM_DATA);
    }
  }, [event]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSave(formData);
  };

  const handleGradeToggle = (grade: number) => {
    setFormData(prev => ({
      ...prev,
      targetGrades: prev.targetGrades?.includes(grade)
        ? prev.targetGrades.filter(g => g !== grade)
        : [...(prev.targetGrades || []), grade]
    }));
  };

  const handleObjectiveAdd = () => {
    const objective = prompt('Enter new objective:');
    if (objective) {
      setFormData(prev => ({
        ...prev,
        objectives: [...(prev.objectives || []), objective]
      }));
    }
  };

  const { getRootProps, getInputProps } = useDropzone({
    onDrop: (acceptedFiles) => {
      const newResources = acceptedFiles.map(file => ({
        id: `resource-${Date.now()}-${file.name}`,
        name: file.name,
        type: 'file',
        url: URL.createObjectURL(file)
      }));
      setFormData(prev => ({
        ...prev,
        // eventResources: [...(prev.eventResources || []), ...newResources]
        eventResources: []
      }));
    }
  });

  return (
    <Dialog 
      open={open} 
      onClose={onClose}
      maxWidth="md"
      fullWidth
      TransitionProps={{
        timeout: 300
      }}
      scroll="paper"
      PaperProps={{
        elevation: 8,
        sx: {
          background: 'linear-gradient(145deg, #ffffff 0%, #f8f9ff 100%)',
          borderRadius: 3,
          display: 'flex',
          flexDirection: 'column',
          maxHeight: '90vh',
          transition: 'transform 0.3s ease-in-out'
        }
      }}
    >
      <form onSubmit={handleSubmit}>
        <DialogTitle sx={{
          background: 'linear-gradient(145deg, #f3f6ff 0%, #ffffff 100%)',
          borderBottom: '1px solid',
          borderColor: 'divider',
          px: 3,
          py: 2.5
        }}>
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
          >
            <Typography 
              variant="h5" 
              sx={{ 
                fontWeight: 600,
                background: theme => 
                  `linear-gradient(45deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent'
              }}
            >
              {event ? 'Edit Event' : 'Add New Event'}
            </Typography>
            <IconButton 
              onClick={onClose} 
              size="small"
              sx={{
                transition: 'transform 0.2s',
                '&:hover': {
                  transform: 'rotate(90deg)'
                }
              }}
            >
              <CloseIcon />
            </IconButton>
          </Stack>
        </DialogTitle>

        <DialogContent sx={{ 
          px: 3, 
          py: 4,
          overflowY: 'auto',
          '&::-webkit-scrollbar': {
            width: 8
          },
          '&::-webkit-scrollbar-track': {
            bgcolor: 'background.default',
            borderRadius: 4
          },
          '&::-webkit-scrollbar-thumb': {
            bgcolor: 'primary.light',
            borderRadius: 4,
            '&:hover': {
              bgcolor: 'primary.main'
            }
          }
        }}>
          <Stack spacing={3}>
            <TextField
              label="Title"
              value={formData.title}
              onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              required
              fullWidth
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                  transition: 'all 0.2s',
                  '&:hover': {
                    bgcolor: 'action.hover',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.05)'
                  },
                  '&.Mui-focused': {
                    boxShadow: theme => `0 0 0 2px ${theme.palette.primary.main}20`
                  }
                }
              }}
            />

            <TextField
              label="Description"
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              multiline
              rows={3}
              fullWidth
              sx={{
                '& .MuiOutlinedInput-root': {
                  transition: 'all 0.2s',
                  '&:hover': {
                    bgcolor: 'action.hover'
                  },
                  '&.Mui-focused': {
                    bgcolor: 'background.paper',
                    boxShadow: 1
                  }
                }
              }}
            />

            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <DateTimePicker
                  label="Start Date & Time"
                  value={new Date(formData?.startDate ?? '')}
                  onChange={(date) => setFormData({ 
                    ...formData, 
                    startDate: date?.toISOString() || new Date().toISOString() 
                  })}
                  sx={{ width: '100%' }}
                  slotProps={{
                    textField: {
                      sx: {
                        '& .MuiOutlinedInput-root': {
                          transition: 'all 0.2s',
                          '&:hover': {
                            bgcolor: 'action.hover'
                          },
                          '&.Mui-focused': {
                            bgcolor: 'background.paper',
                            boxShadow: 1
                          }
                        }
                      }
                    }
                  }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <DateTimePicker
                  label="End Date & Time"
                  value={new Date(formData?.endDate ?? '')}
                  onChange={(date) => setFormData({ 
                    ...formData, 
                    endDate: date?.toISOString() || new Date().toISOString() 
                  })}
                  sx={{ width: '100%' }}
                  slotProps={{
                    textField: {
                      sx: {
                        '& .MuiOutlinedInput-root': {
                          transition: 'all 0.2s',
                          '&:hover': {
                            bgcolor: 'action.hover'
                          },
                          '&.Mui-focused': {
                            bgcolor: 'background.paper',
                            boxShadow: 1
                          }
                        }
                      }
                    }
                  }}
                />
              </Grid>
            </Grid>

            <FormControl fullWidth>
              <InputLabel>Event Type</InputLabel>
              <Select
                value={formData.eventCategory}
                label="Event Type"
                onChange={(e) => setFormData({ ...formData, eventCategory: e.target.value as EventCategory })}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    transition: 'all 0.2s',
                    '&:hover': {
                      bgcolor: 'action.hover'
                    },
                    '&.Mui-focused': {
                      bgcolor: 'background.paper',
                      boxShadow: 1
                    }
                  }
                }}
              >
                {EVENT_TYPES.map(type => (
                  <MenuItem key={type.value} value={type.value}>
                    {type.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl fullWidth>
              <InputLabel>Location Type</InputLabel>
              <Select
                value={formData.locationType}
                label="Location Type"
                onChange={(e) => setFormData({ ...formData, locationType: e.target.value as LocationType })}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    transition: 'all 0.2s',
                    '&:hover': {
                      bgcolor: 'action.hover'
                    },
                    '&.Mui-focused': {
                      bgcolor: 'background.paper',
                      boxShadow: 1
                    }
                  }
                }}
              >
                {LOCATION_TYPES.map(type => (
                  <MenuItem key={type.value} value={type.value}>
                    {type.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <TextField
              label="Location"
              value={formData.location}
              onChange={(e) => setFormData({ ...formData, location: e.target.value })}
              fullWidth
            />

            {(formData.locationType === 'virtual' || formData.locationType === 'hybrid') && (
              <TextField
                label="Virtual Meeting Link"
                value={formData.virtualLink}
                onChange={(e) => setFormData({ ...formData, virtualLink: e.target.value })}
                fullWidth
              />
            )}

            <FormControl fullWidth>
              <InputLabel>Status</InputLabel>
              <Select
                value={formData.status}
                label="Status"
                onChange={(e) => setFormData({ ...formData, status: e.target.value as EventStatus })}
              >
                {EVENT_STATUSES.map(status => (
                  <MenuItem key={status.value} value={status.value}>
                    {status.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <Box>
              <Typography variant="subtitle2" gutterBottom>
                Target Grades
              </Typography>
              <Stack direction="row" spacing={1}>
                {GRADES.map(grade => (
                  <Chip
                    key={grade}
                    label={`Grade ${grade}`}
                    onClick={() => handleGradeToggle(grade)}
                    color={formData.targetGrades?.includes(grade) ? 'primary' : 'default'}
                    variant={formData.targetGrades?.includes(grade) ? 'filled' : 'outlined'}
                  />
                ))}
              </Stack>
            </Box>

            <Box>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                <Typography variant="subtitle2">
                  Objectives
                </Typography>
                <Button
                  startIcon={<AddIcon />}
                  onClick={handleObjectiveAdd}
                  size="small"
                >
                  Add Objective
                </Button>
              </Box>
              <Stack spacing={1}>
                {formData.objectives?.map((objective, index) => (
                  <Chip
                    key={index}
                    label={objective}
                    onDelete={() => setFormData(prev => ({
                      ...prev,
                      objectives: prev.objectives?.filter((_, i) => i !== index)
                    }))}
                  />
                ))}
              </Stack>
            </Box>

            <Box>
              <Typography variant="subtitle2" gutterBottom>
                Resources
              </Typography>
              <Box
                {...getRootProps()}
                sx={{
                  p: 2,
                  border: '2px dashed',
                  borderColor: 'divider',
                  borderRadius: 1,
                  textAlign: 'center',
                  cursor: 'pointer',
                  '&:hover': {
                    borderColor: 'primary.main'
                  }
                }}
              >
                <input {...getInputProps()} />
                <Typography>
                  Drag & drop files here, or click to select files
                </Typography>
              </Box>
              <Stack spacing={1} sx={{ mt: 2 }}>
                {formData.eventResources?.map((resource) => (
                  <Chip
                    key={resource.id}
                    label={resource.name}
                    onDelete={() => setFormData(prev => ({
                      ...prev,
                      eventResources: prev.eventResources?.filter(r => r.id !== resource.id)
                    }))}
                  />
                ))}
              </Stack>
            </Box>
          </Stack>
        </DialogContent>

        <DialogActions sx={{
          position: 'sticky',
          bottom: 0,
          px: 3,
          py: 2.5,
          borderTop: '1px solid',
          borderColor: 'divider',
          bgcolor: 'background.default'
        }}>
          <Button 
            onClick={onClose}
            sx={{
              borderRadius: 2,
              px: 3,
              transition: 'all 0.2s',
              '&:hover': {
                bgcolor: 'action.hover',
                transform: 'translateY(-1px)'
              }
            }}
          >
            Cancel
          </Button>
          <Button 
            type="submit" 
            variant="contained" 
            color="primary"
            sx={{
              borderRadius: 2,
              px: 4,
              background: theme => 
                `linear-gradient(45deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
              boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
              transition: 'all 0.2s',
              '&:hover': {
                transform: 'translateY(-2px)',
                boxShadow: '0 6px 16px rgba(0,0,0,0.2)'
              },
              '&:active': {
                transform: 'translateY(0)'
              }
            }}
          >
            {event ? 'Save Changes' : 'Create Event'}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};